import { css, Theme } from '@emotion/react';
import { type maps } from '@birdi/js-sdk';
import { BirdiTheme, blocks } from '@birdi/theme';
import { ContextMenu, contextStyles } from '@birdi/context-menu/ContextMenu';
import { deleteMap } from '@birdi/js-sdk/src/public/maps';
import { APIResponse } from '@birdi/js-sdk/src/helpers';
import { useState } from 'react';
import MapRename from './MapRenameForm';
import { MapShare } from '@birdi/map-share';
import { useMetaSelector } from '../meta/reducer';
import { CreateMissionCard } from '../dashboard/create-mission-card';
import { MoveToFolderModal } from './move-to-folder-modal';
import { MoveToWorkspaceModal } from './move-to-workspace-modal';
import { MoveContextMenu } from './move-context-menu';
import { DuplicateMapModal } from './duplicate-map-modal';
import { AddToSiteModal } from './add-to-site-modal';
import MapPinSVG from '@birdi/icons/map-pin.svg';
import CalendarSVG from '@birdi/icons/calendar.svg';
import StorageSVG from '@birdi/icons/hard-drive.svg';
import UserSVG from '@birdi/icons/user.svg';
import FolderSVG from '@birdi/icons/folder.svg';
import RenameSVG from '@birdi/icons/rename.svg';
import LinkSVG from '@birdi/icons/link.svg';
import CopySVG from '@birdi/icons/copy.svg';
import TrashSVG from '@birdi/icons/trash-2.svg';
import SitesGlobeSVG from '@birdi/icons/sites-globe.svg';
import { hasSites } from '../map-sites/site-features';

export interface MissionListProps {
  maps: maps.Map[];
  isDashboard: boolean;
}

export const NewMapList = ({ maps, isDashboard = false }: MissionListProps) => {
  return (
    <div css={blocks.cardList}>
      {maps.map((map, index) => (
        <MapCard key={index} map={map} />
      ))}
      {isDashboard && <CreateMissionCard newMaps />}
    </div>
  );
};

type MapCardProps = {
  map: maps.Map;
};

const MapCard: React.FC<MapCardProps> = ({ map }) => {
  // console.log('map: ', map)
  const handleDelete = () => {
    if (
      window.confirm(
        'Are you sure you want to delete this map? This action cannot be undone. Any data, processing jobs, or reports associated with this map will no longer be available.',
      )
    ) {
      if (map.id) {
        console.log('deleting map: ', map.id);
        deleteMap({ tag: 'k1665s' }, map.id).then(
          (response: APIResponse<void>) => {
            if (response.status === 200) {
              window.location.reload();
            } else {
              window.alert('Cannot delete map.');
            }
          },
        );
      } else {
        console.log('delete failed');
      }
    }
  };

  const handleShareButtonClick = () => {
    setShareModalOpen(true);
    //window.alert('need to implement');
  };

  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [moveToFolderModalOpen, setMoveToFolderModalOpen] = useState(false);
  const [moveToWorkspaceModalOpen, setMoveToWorkspaceModalOpen] =
    useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [addToSiteModalOpen, setAddToSiteModalOpen] = useState(false);
  const meta = useMetaSelector();
  const canCreateShareLink = meta.features?.includes('share-view-only');
  const canUseSites = hasSites(meta.features);
  const currentWorkspaceId = meta.workspace?.id || meta.organisation.id || null;
  // Duplicating needs edit access on the map (the API enforces map:edit), so
  // view-only roles such as Guest don't get an option that would only 403.
  // Keep in step with the same gate in new-map-table.tsx.
  const canDuplicate = Boolean(meta.permissions?.includes('map:edit'));
  // The share-link API needs map:share:public, the same check as the map
  // header's share button. Keep in step with new-map-table.tsx.
  const canShare = Boolean(meta.permissions?.includes('map:share:public'));

  const menuItems = [
    {
      label: 'Rename map',
      icon: <RenameSVG />,
      onClick: () => setRenameModalOpen(true),
    },
    ...(canShare
      ? [
          {
            label: 'Share map',
            icon: <LinkSVG />,
            onClick: () => setShareModalOpen(true),
          },
        ]
      : []),
    ...(canUseSites
      ? [
          {
            label: 'Add to site',
            icon: <SitesGlobeSVG />,
            onClick: () => setAddToSiteModalOpen(true),
          },
        ]
      : []),
    ...(canDuplicate
      ? [
          {
            label: 'Duplicate map',
            icon: <CopySVG />,
            onClick: () => setDuplicateModalOpen(true),
          },
        ]
      : []),
    {
      label: 'Delete map',
      icon: <TrashSVG />,
      onClick: handleDelete,
    },
  ];

  const storageData = () => {
    const gb = Number(map.totalGB);
    if (!isNaN(gb)) {
      if (gb < 1) {
        return `${map.fileCount || '-'} files | ${(gb * 1024).toFixed(2)} MB`;
      } else {
        return `${map.fileCount || '-'} files | ${gb.toFixed(2)} GB`;
      }
    } else {
      return `${map.fileCount || '-'} files | - GB`;
    }
  };

  return (
    <div
      css={blocks.sharedMapCardStyle}
      onClick={() => {
        if (
          !renameModalOpen &&
          !shareModalOpen &&
          !moveToFolderModalOpen &&
          !moveToWorkspaceModalOpen &&
          !duplicateModalOpen &&
          !addToSiteModalOpen
        ) {
          window.location.href = `/map/next/${map.id}`;
        }
      }}
    >
      <MapRename
        open={renameModalOpen}
        onChange={setRenameModalOpen}
        initialName={map.name}
        mapId={map.id}
      ></MapRename>
      <MapShare
        open={shareModalOpen}
        onChange={setShareModalOpen}
        mapId={map.id}
        disabled={!canCreateShareLink}
      ></MapShare>
      <MoveToFolderModal
        open={moveToFolderModalOpen}
        onChange={setMoveToFolderModalOpen}
        mapId={map.id}
        currentFolderId={map.folderId || null}
      />
      <DuplicateMapModal
        open={duplicateModalOpen}
        onChange={setDuplicateModalOpen}
        mapId={map.id}
        mapName={map.name}
      />
      <AddToSiteModal
        open={addToSiteModalOpen}
        onChange={setAddToSiteModalOpen}
        mapId={map.id}
        mapName={map.name}
      />
      <MoveToWorkspaceModal
        open={moveToWorkspaceModalOpen}
        onChange={setMoveToWorkspaceModalOpen}
        mapId={map.id}
        currentWorkspaceId={currentWorkspaceId}
      />
      <div
        className="card-cover"
        css={css`
          background-image: url('${map.mapCover}');
        `}
      />

      <div className="card-body">
        <div className="card-info">
          <div
            css={css`
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: flex-start;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                font-size: 0.9rem;
                position: relative;
                width: 70%;
              `}
            >
              <h3
                data-testid="name"
                css={css`
                  text-transform: capitalize;
                  margin: 0;
                  margin-bottom: 0.5em;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {map.name || 'Untitled Map'}
              </h3>
              <div
                css={css`
                  span {
                    display: flex;
                    gap: 0.5em;
                    svg {
                      margin-top: 4px;
                    }
                  }
                `}
              >
                <span>
                  <MapPinSVG />
                  {map.location?.name || 'Unknown'}
                </span>
                <span>
                  <CalendarSVG />
                  {new Date(map.createdAt).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
                <span>
                  <UserSVG />
                  {map.creatorFname || '-'} {map.creatorLname || '-'}
                </span>
                <span>
                  <StorageSVG
                    css={(theme: BirdiTheme) => css`
                      color: ${theme.midnight};
                    `}
                  />
                  {storageData()}
                </span>
                <span>
                  <FolderSVG />
                  {map.folderId ? map.folderName : '-'}
                </span>
              </div>
            </div>
            <div
              css={css`
                position: relative;
              `}
              onClick={(event: any) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ContextMenu alignRight>
                {() => (
                  <div
                    css={(theme) => css`
                      ${contextStyles(theme)};
                      min-width: 180px;
                    `}
                  >
                    {menuItems.slice(0, 2).map((item, index) => (
                      <button
                        key={index}
                        type="button"
                        css={(theme) => css`
                          display: flex;
                          align-items: center;
                          gap: 0.5rem;
                          width: 100%;
                          padding: 0.5rem 0.75rem;
                          border: none;
                          background: none;
                          cursor: pointer;
                          text-align: left;
                          &:hover {
                            background-color: ${theme.sand};
                          }
                        `}
                        onClick={item.onClick}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </button>
                    ))}
                    <MoveContextMenu
                      onMoveToWorkspace={() =>
                        setMoveToWorkspaceModalOpen(true)
                      }
                      onMoveToFolder={() => setMoveToFolderModalOpen(true)}
                    />
                    {menuItems.slice(2).map((item, index) => (
                      <button
                        key={index + 2}
                        type="button"
                        css={(theme) => css`
                          display: flex;
                          align-items: center;
                          gap: 0.5rem;
                          width: 100%;
                          padding: 0.5rem 0.75rem;
                          border: none;
                          background: none;
                          cursor: pointer;
                          text-align: left;
                          &:hover {
                            background-color: ${theme.sand};
                          }
                        `}
                        onClick={item.onClick}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </ContextMenu>
            </div>
          </div>
        </div>
        {/* <div className="card-version">
        <div
          css={css`
            width: 100px;
            background-color: #EFFBF5;
            border-radius: 8px;
            padding: 3px;
          `}
        >
          <p css={css`margin: 0;`}>Birdi map 2.0</p>
        </div>
      </div> */}
      </div>
    </div>
  );
};
